import React, { useState } from 'react';
import { Table } from 'flowbite-react';
import { TableCell, TableCommon } from '../components/Table/Table';
import { useTranslation } from 'react-i18next';
import {
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
  useFetchNotificationConfigurationQuery,
} from '../redux/api/notificationConfigurationApi';
import { INotification } from '../common/interfaces/NotificationConfiguration.interface';
import { Loading } from '../components/Shared/Loading/Loading';
import { usePagination } from '../hooks/usePagination';
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';
import { NotificationEditModal } from '../components/NotificationModal/NotificationModal';
import { EditButton } from '../components/Shared/EditButton';
import { PageHeader } from '../components/Shared/PageHeader';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { usePageTitle } from '../hooks/usePageTitle';

export const NotificationConfigurationPage: React.FC = () => {
  const { t } = useTranslation();

  usePageTitle(t('pageTitles.notificationConfiguration'));

  const [selectedNotification, setSelectedNotification] =
    useState<INotification | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { currentPage, setCurrentPage, size } = usePagination();

  const columnHeadLabels = [
    { id: 'title', label: t('notification.title') },
    { id: 'code', label: t('notification.code') },
    { id: 'status', label: t('contentContext.status') },
    { id: 'edit', label: t('contentContext.edit') },
  ];

  const {
    data: response,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useFetchNotificationConfigurationQuery({
    page: currentPage,
    size,
  });

  const [editNotification] = useUpdateNotificationMutation();
  const [createNotification] = useCreateNotificationMutation();

  const notificationList: INotification[] = response?.data || [];

  const handleEditClick = (notification: INotification) => {
    setSelectedNotification(notification);
    setIsModalOpen(true);
  };
  const handleCreateClick = () => {
    setSelectedNotification(null);
    setIsModalOpen(true);
  };
  const handleSave = async (updatedNotification: INotification) => {
    try {
      if (selectedNotification?.id && updatedNotification.id) {
        await editNotification({
          id: updatedNotification.id,
          updatedNotification: { ...updatedNotification },
        }).unwrap();
      } else {
        await createNotification(updatedNotification).unwrap();
      }
      setIsModalOpen(false);
      setSelectedNotification(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.errors?.[0] || 'An unknown error occurred';
      alert(errorMessage);
      setIsModalOpen(false);
      setSelectedNotification(null);
    }
  };

  if (isError) return <ErrorComponent error={loadError} onRetry={refetch} />;
  if (isLoading) return <Loading />;
  return (
    <>
      <PageHeader
        title={t('notification.notification')}
        buttonText={t('notification.addNotification')}
        buttonOnClick={handleCreateClick}
      />
      <TableCommon columns={columnHeadLabels}>
        {notificationList.map((item) => (
          <Table.Row key={item.id} className="bg-white">
            <TableCell>{item.title[0].value}</TableCell>
            <TableCell>{item.code}</TableCell>
            <TableCell>{t(`status.${item.status}`)}</TableCell>
            <TableCell>
              <EditButton onClick={() => handleEditClick(item)} />
            </TableCell>
          </Table.Row>
        ))}
      </TableCommon>
      {/* <CustomPagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      /> */}
      <NotificationEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        notification={selectedNotification}
        onSave={handleSave}
        mode={selectedNotification ? 'edit' : 'create'}
      />
    </>
  );
};
